
import { useState, useCallback } from 'react';

interface PhotoGalleryCarouselProps {
  images: string[];
  eventTitle: string;
}

export default function PhotoGalleryCarousel({ images, eventTitle }: PhotoGalleryCarouselProps) {
  const [activeIndex, setActiveIndex] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  const prev = useCallback(() => {
    setActiveIndex(i => (i === 0 ? images.length - 1 : i - 1));
  }, [images.length]);

  const next = useCallback(() => {
    setActiveIndex(i => (i === images.length - 1 ? 0 : i + 1));
  }, [images.length]);

  const openLightbox = (index: number) => {
    setLightboxIndex(index);
    setLightboxOpen(true);
  };

  const closeLightbox = () => setLightboxOpen(false);

  const lightboxPrev = () => setLightboxIndex(i => (i === 0 ? images.length - 1 : i - 1));
  const lightboxNext = () => setLightboxIndex(i => (i === images.length - 1 ? 0 : i + 1));

  if (!images || images.length === 0) return null;

  return (
    <div className="mb-10">
      <h2 className="text-2xl font-bold text-gray-900 mb-5 flex items-center gap-2">
        <div className="w-8 h-8 flex items-center justify-center">
          <i className="ri-image-line text-teal-600 text-2xl"></i>
        </div>
        <span>Photo Gallery</span>
        <span className="ml-2 text-sm font-normal text-gray-400 bg-gray-100 px-2.5 py-0.5 rounded-full">
          {images.length} photos
        </span>
      </h2>

      {/* Main Carousel */}
      <div className="relative rounded-2xl overflow-hidden shadow-md bg-gray-900 group">
        <div className="relative h-[420px] w-full overflow-hidden">
          {images.map((src, idx) => (
            <div
              key={idx}
              className={`absolute inset-0 transition-opacity duration-500 ${
                idx === activeIndex ? 'opacity-100 z-10' : 'opacity-0 z-0'
              }`}
            >
              <img
                src={src}
                alt={`${eventTitle} - photo ${idx + 1}`}
                className="w-full h-full object-cover object-top cursor-zoom-in"
                onClick={() => openLightbox(idx)}
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent pointer-events-none" />
            </div>
          ))}
        </div>

        {/* Expand hint */}
        <button
          onClick={() => openLightbox(activeIndex)}
          className="absolute top-4 right-4 z-20 bg-black/40 hover:bg-black/60 text-white rounded-lg px-3 py-1.5 text-xs font-medium flex items-center gap-1.5 transition-colors cursor-pointer whitespace-nowrap"
        >
          <div className="w-4 h-4 flex items-center justify-center">
            <i className="ri-fullscreen-line text-sm"></i>
          </div>
          View Full Size
        </button>

        {/* Prev / Next */}
        {images.length > 1 && (
          <>
            <button
              onClick={prev}
              className="absolute left-4 top-1/2 -translate-y-1/2 z-20 w-10 h-10 flex items-center justify-center bg-white/90 hover:bg-white rounded-full shadow-lg transition-all opacity-0 group-hover:opacity-100 cursor-pointer"
              aria-label="Previous photo"
            >
              <i className="ri-arrow-left-s-line text-gray-800 text-xl"></i>
            </button>
            <button
              onClick={next}
              className="absolute right-4 top-1/2 -translate-y-1/2 z-20 w-10 h-10 flex items-center justify-center bg-white/90 hover:bg-white rounded-full shadow-lg transition-all opacity-0 group-hover:opacity-100 cursor-pointer"
              aria-label="Next photo"
            >
              <i className="ri-arrow-right-s-line text-gray-800 text-xl"></i>
            </button>
          </>
        )}

        {/* Dot indicators */}
        {images.length > 1 && (
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-20 flex items-center gap-2">
            {images.map((_, idx) => (
              <button
                key={idx}
                onClick={() => setActiveIndex(idx)}
                className={`rounded-full transition-all cursor-pointer ${
                  idx === activeIndex
                    ? 'w-6 h-2 bg-white'
                    : 'w-2 h-2 bg-white/50 hover:bg-white/80'
                }`}
                aria-label={`Go to photo ${idx + 1}`}
              />
            ))}
          </div>
        )}
      </div>

      {/* Thumbnail Strip */}
      {images.length > 1 && (
        <div className="flex gap-3 mt-3 overflow-x-auto pb-1">
          {images.map((src, idx) => (
            <button
              key={idx}
              onClick={() => setActiveIndex(idx)}
              className={`flex-shrink-0 w-20 h-16 rounded-lg overflow-hidden border-2 transition-all cursor-pointer ${
                idx === activeIndex
                  ? 'border-teal-500 shadow-md scale-105'
                  : 'border-transparent opacity-60 hover:opacity-100'
              }`}
            >
              <img
                src={src}
                alt={`Thumbnail ${idx + 1}`}
                className="w-full h-full object-cover object-top"
              />
            </button>
          ))}
        </div>
      )}

      {/* Lightbox */}
      {lightboxOpen && (
        <div
          className="fixed inset-0 z-[100] bg-black/95 flex items-center justify-center"
          onClick={closeLightbox}
        >
          <div
            className="relative max-w-5xl w-full mx-4"
            onClick={e => e.stopPropagation()}
          >
            {/* Close */}
            <button
              onClick={closeLightbox}
              className="absolute -top-12 right-0 w-10 h-10 flex items-center justify-center text-white/70 hover:text-white transition-colors cursor-pointer z-10"
              aria-label="Close lightbox"
            >
              <i className="ri-close-line text-2xl"></i>
            </button>

            {/* Counter */}
            <div className="absolute -top-10 left-0 text-white/60 text-sm">
              {lightboxIndex + 1} / {images.length}
            </div>

            {/* Image */}
            <div className="rounded-xl overflow-hidden">
              <img
                src={images[lightboxIndex]}
                alt={`${eventTitle} - photo ${lightboxIndex + 1}`}
                className="w-full max-h-[80vh] object-contain bg-black"
              />
            </div>

            {/* Nav */}
            {images.length > 1 && (
              <>
                <button
                  onClick={lightboxPrev}
                  className="absolute left-4 top-1/2 -translate-y-1/2 w-11 h-11 flex items-center justify-center bg-white/10 hover:bg-white/25 rounded-full text-white transition-colors cursor-pointer"
                  aria-label="Previous"
                >
                  <i className="ri-arrow-left-s-line text-2xl"></i>
                </button>
                <button
                  onClick={lightboxNext}
                  className="absolute right-4 top-1/2 -translate-y-1/2 w-11 h-11 flex items-center justify-center bg-white/10 hover:bg-white/25 rounded-full text-white transition-colors cursor-pointer"
                  aria-label="Next"
                >
                  <i className="ri-arrow-right-s-line text-2xl"></i>
                </button>
              </>
            )}

            {/* Lightbox thumbnails */}
            {images.length > 1 && (
              <div className="flex justify-center gap-2 mt-4">
                {images.map((src, idx) => (
                  <button
                    key={idx}
                    onClick={() => setLightboxIndex(idx)}
                    className={`w-14 h-10 rounded overflow-hidden border-2 transition-all cursor-pointer flex-shrink-0 ${
                      idx === lightboxIndex ? 'border-teal-400' : 'border-transparent opacity-50 hover:opacity-80'
                    }`}
                  >
                    <img src={src} alt="" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
